import React, { createContext, useContext, useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

const ToastContext = createContext(null)

let toastIdCounter = 0

const MAX_TOASTS = 5
const VISIBLE_WHEN_STACKED = 3
const GAP = 10
const PEEK = 10
const EXIT_MS = 340
const SWIPE_THRESHOLD = 80

const ACCENTS = {
  success: 'var(--token-success, #00C4A9)',
  error: 'var(--token-error, #FF6634)',
  warning: 'var(--token-warning, #D9C800)',
  info: 'var(--token-info, #1946FA)',
}

// Backend errors often end with a raw provider payload ("Failed to X: Azure ... (400): {json}").
// Users see only the leading clause; the full text stays one click away for support.
function splitMessage(message) {
  if (typeof message !== 'string') return { summary: message, technical: null }
  const start = message.search(/[{[]/)
  if (start < 0 || message.length - start < 20) return { summary: message, technical: null }
  const lead = message.slice(0, start).split(':')[0].trim()
  const summary = lead ? (/[.!?]$/.test(lead) ? lead : `${lead}.`) : 'Something went wrong. Please try again.'
  return { summary, technical: message }
}

function useDocumentHidden() {
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden)
  useEffect(() => {
    const onChange = () => setHidden(document.hidden)
    document.addEventListener('visibilitychange', onChange)
    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])
  return hidden
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const [heights, setHeights] = useState({})
  const [expanded, setExpanded] = useState(false)
  const [dragging, setDragging] = useState(false)
  const hidden = useDocumentHidden()

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => (t.id === id ? { ...t, exiting: true } : t)))
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
      setHeights(prev => {
        if (!(id in prev)) return prev
        const next = { ...prev }
        delete next[id]
        return next
      })
    }, EXIT_MS)
  }, [])

  // Mirrors `toasts` so addToast can check for duplicates synchronously,
  // including two calls in the same tick before a render.
  const toastsRef = useRef(toasts)
  toastsRef.current = toasts

  const addToast = useCallback(({ type = 'info', title, message, duration = 6000, action = null }) => {
    // A locked field's overlay re-fires onBlockedClick on every click (e.g.
    // repeatedly clicking a read-only row), which would otherwise stack an
    // identical toast on top of the one still on screen. Restart the
    // existing one's countdown instead of piling on a duplicate.
    const existing = toastsRef.current.find(t => !t.exiting && t.type === type && t.title === title && t.message === message)
    if (existing) {
      const refreshed = { ...existing, refresh: (existing.refresh || 0) + 1 }
      toastsRef.current = toastsRef.current.map(t => (t.id === existing.id ? refreshed : t))
      setToasts(prev => prev.map(t => (t.id === existing.id ? { ...t, refresh: refreshed.refresh } : t)))
      return existing.id
    }

    const id = ++toastIdCounter
    const toast = { id, type, title, message, duration, action, exiting: false, refresh: 0 }
    toastsRef.current = [...toastsRef.current, toast]
    setToasts(prev => [...prev, toast])
    return id
  }, [])

  const success = useCallback((title, message) => addToast({ type: 'success', title, message }), [addToast])
  const error = useCallback((title, message) => addToast({ type: 'error', title, message, duration: 8000 }), [addToast])
  const info = useCallback((title, message) => addToast({ type: 'info', title, message }), [addToast])
  const warning = useCallback((title, message) => addToast({ type: 'warning', title, message, duration: 7000 }), [addToast])

  const live = toasts.filter(t => !t.exiting)

  useEffect(() => {
    if (live.length > MAX_TOASTS) removeToast(live[0].id)
    if (live.length === 0) setExpanded(false)
  }, [live.length, removeToast]) // eslint-disable-line react-hooks/exhaustive-deps

  const setHeight = useCallback((id, h) => {
    setHeights(prev => (prev[id] === h ? prev : { ...prev, [id]: h }))
  }, [])

  // Newest first: index 0 is the front card.
  const layout = {}
  let offset = 0
  ;[...live].reverse().forEach((t, index) => {
    layout[t.id] = { index, offset }
    offset += (heights[t.id] || 0) + GAP
  })
  const frontId = live[live.length - 1]?.id
  const frontHeight = heights[frontId] || 0
  const stackHeight = expanded ? Math.max(offset - GAP, 0) : frontHeight
  const paused = expanded || dragging || hidden

  return (
    <ToastContext.Provider value={{ addToast, removeToast, success, error, info, warning }}>
      {children}

      {createPortal(
        <section
          aria-label="Notifications"
          aria-live="polite"
          className="toast-viewport"
          onMouseEnter={() => setExpanded(true)}
          onMouseLeave={() => { if (!dragging) setExpanded(false) }}
          onFocus={() => setExpanded(true)}
          onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setExpanded(false) }}
        >
          <ol className="toast-stack" style={{ height: stackHeight }}>
            {toasts.map(toast => (
              <ToastItem
                key={toast.id}
                toast={toast}
                slot={layout[toast.id]}
                expanded={expanded}
                frontHeight={frontHeight}
                ownHeight={heights[toast.id]}
                paused={paused}
                onHeight={setHeight}
                onDragChange={setDragging}
                onClose={removeToast}
              />
            ))}
          </ol>
        </section>,
        document.body
      )}
    </ToastContext.Provider>
  )
}

function ToastItem({ toast, slot, expanded, frontHeight, ownHeight, paused, onHeight, onDragChange, onClose }) {
  const { id, type, title, message, action, duration, exiting, refresh } = toast
  const accent = ACCENTS[type] || ACCENTS.info
  const contentRef = useRef(null)
  const [mounted, setMounted] = useState(false)
  const [dragX, setDragX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [swipeOut, setSwipeOut] = useState(0)
  const [copied, setCopied] = useState(false)
  const drag = useRef(null)
  const { summary, technical } = useMemo(() => splitMessage(message), [message])

  const copyTechnical = async () => {
    try {
      await navigator.clipboard.writeText(technical)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch { /* clipboard blocked */ }
  }
  const sideAction = action
    ? { label: action.label, onClick: () => { try { action.onClick?.() } finally { onClose(id) } } }
    : technical
      ? { label: copied ? 'Copied' : 'Copy error', onClick: copyTechnical }
      : null
  const lastSlot = useRef(slot || { index: 0, offset: 0 })
  if (slot) lastSlot.current = slot
  const { index, offset } = lastSlot.current

  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return undefined
    const measure = () => onHeight(id, el.offsetHeight)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [id, onHeight])

  useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  // Pausable auto-dismiss: the remaining time carries over across pauses.
  // A duplicate toast bumps `refresh`, which restarts the full countdown
  // (this effect runs after the timer's cleanup, before its setup).
  const remaining = useRef(duration)
  useEffect(() => { remaining.current = duration }, [refresh, duration])
  useEffect(() => {
    if (!duration || exiting || paused) return undefined
    const startedAt = Date.now()
    const timer = setTimeout(() => onClose(id), remaining.current)
    return () => {
      clearTimeout(timer)
      remaining.current -= Date.now() - startedAt
    }
  }, [duration, exiting, paused, id, onClose, refresh])

  const onPointerDown = (e) => {
    if (e.button !== 0 || exiting || e.target.closest('button')) return
    drag.current = { x: e.clientX, t: Date.now() }
    e.currentTarget.setPointerCapture(e.pointerId)
    setIsDragging(true)
    onDragChange(true)
  }
  const onPointerMove = (e) => {
    if (drag.current) setDragX(e.clientX - drag.current.x)
  }
  const onPointerUp = () => {
    if (!drag.current) return
    const velocity = Math.abs(dragX) / Math.max(Date.now() - drag.current.t, 1)
    drag.current = null
    setIsDragging(false)
    onDragChange(false)
    if (Math.abs(dragX) > SWIPE_THRESHOLD || velocity > 0.5) {
      setSwipeOut(Math.sign(dragX) || 1)
      onClose(id)
    } else {
      setDragX(0)
    }
  }

  const stacked = !expanded && index > 0
  const hiddenInStack = !expanded && index >= VISIBLE_WHEN_STACKED
  const y = expanded ? -offset : -index * PEEK
  const scale = expanded ? 1 : 1 - index * 0.05

  let transform = `translate3d(${dragX}px, ${y}px, 0) scale(${scale})`
  let opacity = hiddenInStack ? 0 : 1 - Math.min(Math.abs(dragX) / 260, 0.6)
  let filter = 'blur(0)'
  if (!mounted) {
    transform = `translate3d(0, calc(${y}px + 110%), 0) scale(${scale * 0.96})`
    opacity = 0
    filter = 'blur(6px)'
  } else if (exiting) {
    transform = swipeOut
      ? `translate3d(${swipeOut * 110}%, ${y}px, 0) scale(${scale})`
      : `translate3d(0, ${y + 28}px, 0) scale(${scale * 0.94})`
    opacity = 0
    filter = 'blur(4px)'
  }

  const height = stacked && frontHeight ? frontHeight : ownHeight
  const isError = type === 'error'

  return (
    <li
      role={isError ? 'alert' : 'status'}
      aria-atomic="true"
      className="toast-item"
      data-type={type}
      data-swiping={isDragging ? 'true' : undefined}
      data-exiting={exiting ? 'true' : undefined}
      style={{
        '--toast-accent': accent,
        transform,
        opacity,
        filter,
        height: height || undefined,
        zIndex: MAX_TOASTS - index,
        pointerEvents: hiddenInStack || exiting ? 'none' : undefined,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div ref={contentRef} className="toast-content" style={{ opacity: stacked ? 0 : 1 }}>
        <div className="toast-main">
          <span className="toast-badge">
            {duration > 0 && (
              <svg className="toast-ring" viewBox="0 0 40 40" aria-hidden="true">
                <circle cx="20" cy="20" r="18.5" pathLength="1" className="toast-ring-track" />
                <circle
                  key={refresh}
                  cx="20"
                  cy="20"
                  r="18.5"
                  pathLength="1"
                  className="toast-ring-bar"
                  style={{ animationDuration: `${duration}ms`, animationPlayState: paused || exiting ? 'paused' : 'running' }}
                />
              </svg>
            )}
            <ToastIcon type={type} />
          </span>

          <div className="toast-body">
            {title && <p className="toast-title">{title}</p>}
            {summary && <p className="toast-message">{summary}</p>}
          </div>
        </div>

        <div className="toast-side" data-single={sideAction ? undefined : 'true'}>
          {sideAction && (
            <button type="button" className="toast-side-btn toast-side-primary" onClick={sideAction.onClick}>
              {sideAction.label}
            </button>
          )}
          <button type="button" className="toast-side-btn" onClick={() => onClose(id)} aria-label="Dismiss notification">
            {sideAction ? 'Dismiss' : <X className="h-4 w-4" strokeWidth={2.25} />}
          </button>
        </div>
      </div>
    </li>
  )
}

function ToastIcon({ type }) {
  return (
    <span className={`toast-icon toast-icon-${type}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
        {type === 'success' && <path className="toast-draw" pathLength="1" d="M6.5 12.5l3.75 3.75L17.5 8.5" />}
        {type === 'error' && (
          <>
            <path className="toast-draw" pathLength="1" d="M8 8l8 8" />
            <path className="toast-draw toast-draw-late" pathLength="1" d="M16 8l-8 8" />
          </>
        )}
        {type === 'warning' && (
          <>
            <path className="toast-draw" pathLength="1" d="M12 7v6" />
            <path className="toast-dot" d="M12 17h.01" />
          </>
        )}
        {(type === 'info' || !ACCENTS[type]) && (
          <>
            <path className="toast-dot" d="M12 7.5h.01" />
            <path className="toast-draw" pathLength="1" d="M12 11v5.5" />
          </>
        )}
      </svg>
    </span>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
